import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  SessionAttendanceView, SessionCompletionView, TrainingBudgetView, TrainingCourseView,
  TrainingEvaluationView, TrainingNeedView, TrainingNominationView, TrainingPriority,
  TrainingSessionView, TrainingStatus,
} from '../types'

const BASE = '/v1/training-needs'

export function useTrainingNeeds(status?: TrainingStatus | '', priority?: TrainingPriority | '') {
  return useQuery({
    queryKey: ['training', 'needs', status, priority],
    queryFn: async () =>
      (await apiClient.get<TrainingNeedView[]>(BASE, {
        params: { status: status || undefined, priority: priority || undefined },
      })).data,
    placeholderData: (previous) => previous,
  })
}

export interface SaveTrainingNeed {
  employeeId: string
  competencyLabel: string
  competencyRef: string | null
  priority: TrainingPriority
  targetDate: string | null
  notes: string | null
}

export function useTrainingNeedMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['training'] })

  return {
    identify: useMutation({
      mutationFn: async (body: SaveTrainingNeed) =>
        (await apiClient.post<TrainingNeedView>(BASE, body)).data,
      onSuccess: invalidate,
    }),
    move: useMutation({
      mutationFn: async ({ id, status }: { id: string; status: TrainingStatus }) =>
        (await apiClient.post<TrainingNeedView>(`${BASE}/${id}/status`, { status })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- Phase 6 part 3: the catalogue ---

const CAT = '/v1/training'

export function useTrainingCourses() {
  return useQuery({
    queryKey: ['training', 'courses'],
    queryFn: async () => (await apiClient.get<TrainingCourseView[]>(`${CAT}/courses`)).data,
  })
}

export function useTrainingSessions() {
  return useQuery({
    queryKey: ['training', 'sessions'],
    queryFn: async () => (await apiClient.get<TrainingSessionView[]>(`${CAT}/sessions`)).data,
  })
}

export function useSessionRoster(sessionId: string | null) {
  return useQuery({
    queryKey: ['training', 'sessions', sessionId, 'roster'],
    enabled: Boolean(sessionId),
    queryFn: async () =>
      (await apiClient.get<TrainingNominationView[]>(
        `${CAT}/sessions/${sessionId}/nominations`)).data,
  })
}

export function useTrainingBudgets() {
  return useQuery({
    queryKey: ['training', 'budgets'],
    queryFn: async () => (await apiClient.get<TrainingBudgetView[]>(`${CAT}/budgets`)).data,
  })
}

export function useTrainingCatalogueMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['training'] })
    // Certificates land in the person's documents; completion closes training needs.
    void queryClient.invalidateQueries({ queryKey: ['documents'] })
  }

  return {
    saveCourse: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<TrainingCourseView>(`${CAT}/courses/${id}`, body)).data
          : (await apiClient.post<TrainingCourseView>(`${CAT}/courses`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires it: no longer offered, still resolving everywhere it is already named. */
    retireCourse: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TrainingCourseView>(`${CAT}/courses/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    saveSession: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<TrainingSessionView>(`${CAT}/sessions`, body)).data,
      onSuccess: invalidate,
    }),
    openSession: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TrainingSessionView>(`${CAT}/sessions/${id}/open`)).data,
      onSuccess: invalidate,
    }),
    cancelSession: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
        (await apiClient.post<TrainingSessionView>(
          `${CAT}/sessions/${id}/cancel`, { reason })).data,
      onSuccess: invalidate,
    }),
    completeSession: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<SessionCompletionView>(`${CAT}/sessions/${id}/complete`)).data,
      onSuccess: invalidate,
    }),
    nominate: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<TrainingNominationView>(
          `${CAT}/sessions/${id}/nominations`, body)).data,
      onSuccess: invalidate,
    }),
    submitNomination: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TrainingNominationView>(
          `${CAT}/nominations/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    decideNomination: useMutation({
      mutationFn: async ({ id, approved, comment }: {
        id: string; approved: boolean; comment?: string
      }) => (await apiClient.post<TrainingNominationView>(
        `${CAT}/nominations/${id}/decision`, { approved, comment })).data,
      onSuccess: invalidate,
    }),
    recordAttendance: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<SessionAttendanceView>(
          `${CAT}/nominations/${id}/attendance`, body)).data,
      onSuccess: invalidate,
    }),
    recordReaction: useMutation({
      mutationFn: async ({ id, score, comments }: {
        id: string; score?: number; comments?: string
      }) => (await apiClient.post<TrainingEvaluationView>(
        `${CAT}/nominations/${id}/reaction`, { score, comments })).data,
      onSuccess: invalidate,
    }),
    recordFollowUp: useMutation({
      mutationFn: async ({ id, score, comments }: {
        id: string; score?: number; comments?: string
      }) => (await apiClient.post<TrainingEvaluationView>(
        `${CAT}/nominations/${id}/follow-up`, { score, comments })).data,
      onSuccess: invalidate,
    }),
    saveBudget: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<TrainingBudgetView>(`${CAT}/budgets`, body)).data,
      onSuccess: invalidate,
    }),
  }
}
